/**
 * Scene Stitching Engine - Sprint 15-16 Phase 5
 * Handles multi-scene video stitching using Mediabunny
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { Button, Modal, Progress, Typography, Space, Alert, Steps, Badge, Tooltip, message } from 'antd';
import { Film, Download, AlertCircle, Share2, Play, CheckCircle, Instagram, Linkedin, Youtube, RefreshCw, Sparkles, Wand2, Volume2, Zap, TrendingUp, Lightbulb } from 'lucide-react';
import {
  activeProjectAtom,
  sceneListAtom,
  stitchingProgressAtom,
} from '../../atoms/sceneAtoms';
import { isExportModalVisibleAtom, exportVideoBlobsAtom } from '../../atoms/exportAtoms';
import { db } from '../../idb/chunk-db';
import SceneService from '../../services/sceneService';
import { CanvasVideoStitcher } from '../../services/canvasVideoStitcher';
import logger from '../../utils/logger';
import { useFeatureFlag } from '../../services/featureFlags';

const { Text } = Typography;
const { Step } = Steps;

interface SceneStitchingEngineProps {
  visible: boolean;
  onClose: () => void;
  onComplete?: (videoBlob: Blob) => void;
}

/**
 * Simple validation to ensure scene blob has data
 * Uses same approach as preview (which works perfectly)
 */
function validateSceneBlob(blob: Blob, sceneIndex: number, sceneTitle: string): {
  valid: boolean;
  error?: string;
} {
  // Simple size check - if preview can play it, it's valid
  if (!blob || blob.size === 0) {
    return {
      valid: false,
      error: `Scene ${sceneIndex + 1} "${sceneTitle}": Recording is empty (0 bytes)`
    };
  }

  logger.info(`[SceneValidation] ✓ Scene ${sceneIndex + 1} "${sceneTitle}": ${(blob.size / 1024 / 1024).toFixed(2)}MB - valid`);
  return { valid: true };
}

export const SceneStitchingEngine: React.FC<SceneStitchingEngineProps> = ({
  visible,
  onClose,
  onComplete,
}) => {
  const [activeProject] = useAtom(activeProjectAtom);
  const [sceneList] = useAtom(sceneListAtom);
  const [stitchingProgress, setStitchingProgress] = useAtom(stitchingProgressAtom);
  const [stitchedBlob, setStitchedBlob] = useState<Blob | null>(null);
  const [, setIsExportModalVisible] = useAtom(isExportModalVisibleAtom);
  const [, setExportVideoBlobs] = useAtom(exportVideoBlobsAtom);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [videoMetadata, setVideoMetadata] = useState<{
    width: number;
    height: number;
    duration: number;
  } | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSuccessView, setShowSuccessView] = useState(false);
  
  const platformExportEnabled = useFeatureFlag('platform-export');

  const validateScenes = useCallback(async (): Promise<boolean> => {
    if (!activeProject) {
      logger.error('[SceneStitchingEngine] No active project');
      return false;
    }

    const recordedScenes = sceneList.filter((s) => s.status === 'recorded');
    if (recordedScenes.length === 0) {
      logger.error('[SceneStitchingEngine] No recorded scenes found');
      setStitchingProgress({
        isStitching: false,
        progress: 0,
        status: 'error',
        error: 'No recorded scenes to stitch',
      });
      return false;
    }

    for (const scene of recordedScenes) {
      const isValid = await SceneService.validateSceneChunkMapping(scene.id);
      if (!isValid) {
        logger.error(`[SceneStitchingEngine] Invalid chunk mapping for scene: ${scene.title}`);
        setStitchingProgress({
          isStitching: false,
          progress: 0,
          status: 'error',
          error: `Missing chunks for scene: ${scene.title}`,
        });
        return false;
      }
    }

    return true;
  }, [activeProject, sceneList, setStitchingProgress]);

  /**
   * Load a scene video from S3 URL or IndexedDB chunks
   */
  const loadSceneVideo = useCallback(async (scene: any, chunkIds: string[]): Promise<Blob> => {
    // Check if scene has S3 URL (cloud-saved video)
    if (scene?.videoGuid && scene.videoGuid.startsWith('http')) {
      logger.info(`[SceneStitchingEngine] Loading scene from S3:`, scene.videoGuid);
      try {
        const response = await fetch(scene.videoGuid);
        if (!response.ok) {
          throw new Error(`Failed to fetch from S3: ${response.status}`);
        }
        const blob = await response.blob();
        logger.info(`[SceneStitchingEngine] ✅ Loaded scene from S3: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
        return blob;
      } catch (error) {
        logger.warn(`[SceneStitchingEngine] Failed to load from S3, falling back to IndexedDB:`, error);
        // Fall through to IndexedDB loading
      }
    }

    // Load from IndexedDB chunks (local storage)
    const sceneChunks: Blob[] = [];
    for (const chunkId of chunkIds) {
      const chunk = await db.chunks.get(chunkId);
      if (chunk?.blob) {
        sceneChunks.push(new Blob([chunk.blob]));
      }
    }
    
    if (sceneChunks.length === 0) {
      throw new Error(`No video data found for scene (neither S3 nor IndexedDB)`);
    }

    const sceneBlob = new Blob(sceneChunks, { type: 'video/webm' });
    logger.info(`[SceneStitchingEngine] ✓ Loaded scene from IndexedDB: ${(sceneBlob.size / 1024 / 1024).toFixed(2)}MB`);
    return sceneBlob;
  }, []);

  const loadSceneChunks = useCallback(async () => {
    if (!activeProject) return { sceneBlobs: [], sceneData: [] };

    // Step 1: Loading (minimum 800ms)
    const loadingStart = Date.now();
    setStitchingProgress({
      isStitching: true,
      progress: 10,
      status: 'loading',
      currentScene: 0,
      totalScenes: sceneList.filter((s) => s.status === 'recorded').length,
    });

    const chunkMapping = await SceneService.getSceneChunkMapping(activeProject.id);
    const sceneBlobs: Blob[] = [];
    const sceneData = chunkMapping.map(m => sceneList.find(s => s.id === m.sceneId)).filter(Boolean);

    logger.info(`[SceneStitchingEngine] Starting to load ${chunkMapping.length} recorded scenes:`, 
      chunkMapping.map((m, i) => {
        const scene = sceneList.find(s => s.id === m.sceneId);
        return `#${i + 1}: ${scene?.title} (${m.chunkIds.length} chunks)`;
      })
    );

    for (let i = 0; i < chunkMapping.length; i++) {
      const mapping = chunkMapping[i];
      const scene = sceneList.find(s => s.id === mapping.sceneId);
      
      setStitchingProgress((prev) => ({
        ...prev,
        progress: 10 + (i / chunkMapping.length) * 30,
        currentScene: i + 1,
      }));

      try {
        // 🆕 Load scene from S3 or IndexedDB
        const sceneBlob = await loadSceneVideo(scene, mapping.chunkIds);
        
        // CRITICAL DEBUG: Test if blob can be read
        console.log(`🔍 SCENE ${i + 1} DEBUG:`, {
          title: scene?.title,
          source: scene?.videoGuid?.startsWith('http') ? 'S3' : 'IndexedDB',
          blobSize: sceneBlob.size,
          blobType: sceneBlob.type
        });
        
        // Test reading the blob to verify it's not corrupted
        try {
          const testSlice = sceneBlob.slice(0, 100);
          const testBuffer = await testSlice.arrayBuffer();
          console.log(`✓ Scene ${i + 1} blob is readable, first 4 bytes:`, new Uint8Array(testBuffer).slice(0, 4));
        } catch (e) {
          console.error(`❌ Scene ${i + 1} blob is CORRUPTED:`, e);
          throw new Error(`Scene ${i + 1} blob is corrupted and cannot be read`);
        }
        
        logger.info(`[SceneStitchingEngine] ✓ Loaded scene #${i + 1}: ${scene?.title} (${sceneBlob.size} bytes)`);
        
        // Simple validation: just check blob has data
        const validation = validateSceneBlob(sceneBlob, i, scene?.title || `Scene ${i + 1}`);
        if (!validation.valid) {
          logger.error(`[SceneStitchingEngine] ❌ Scene validation failed:`, validation.error);
          throw new Error(validation.error || `Scene ${i + 1} is empty`);
        }
        
        sceneBlobs.push(sceneBlob);
      } catch (error) {
        const errorMsg = `Failed to load scene #${i + 1}: ${scene?.title} - ${error}`;
        logger.error(`[SceneStitchingEngine] ✗ ${errorMsg}`);
        throw new Error(errorMsg);
      }
    }

    logger.info(`[SceneStitchingEngine] ✅ Successfully loaded and validated ${sceneBlobs.length} scene videos`);
    
    // Ensure loading step shows for minimum 800ms
    const loadingElapsed = Date.now() - loadingStart;
    if (loadingElapsed < 800) {
      await new Promise((resolve) => setTimeout(resolve, 800 - loadingElapsed));
    }
    
    return { sceneBlobs, sceneData };
  }, [activeProject, sceneList, setStitchingProgress]);

  const stitchChunks = useCallback(async (sceneBlobs: Blob[], sceneData: any[]): Promise<Blob> => {
    logger.info(`[SceneStitchingEngine] Starting Canvas-based stitching for ${sceneBlobs.length} scenes`);
    
    setStitchingProgress((prev) => ({
      ...prev,
      progress: 50,
      status: 'stitching',
    }));

    try {
      const stitcher = new CanvasVideoStitcher(1920, 1080);

      const transitions = sceneData.map(scene => ({
        type: scene?.transitionOut?.type || 'none',
        duration: scene?.transitionOut?.duration || 0,
      }));

      logger.info('[SceneStitchingEngine] Transitions:', transitions);

      const stitchedBlob = await stitcher.stitchVideos(
        sceneBlobs,
        transitions,
        (progress) => {
          setStitchingProgress((prev) => ({
            ...prev,
            progress: 50 + (progress.progress * 0.4),
            currentScene: progress.currentScene,
            totalScenes: progress.totalScenes,
            status: 'stitching',
          }));
        }
      );

      stitcher.destroy();

      logger.info('[SceneStitchingEngine] ✓ Canvas stitching complete:', {
        size: stitchedBlob.size,
        sizeMB: (stitchedBlob.size / 1024 / 1024).toFixed(2)
      });

      setStitchingProgress((prev) => ({
        ...prev,
        progress: 100,
        status: 'complete',
      }));

      return stitchedBlob;
    } catch (error) {
      logger.error('[SceneStitchingEngine] ❌ Canvas stitching failed:', error);
      throw new Error(`Stitching failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [setStitchingProgress]);

  const validateVideoBlob = useCallback(async (blob: Blob): Promise<boolean> => {
    return new Promise((resolve) => {
      logger.info('[SceneStitchingEngine] Validating video blob...', {
        size: blob.size,
        type: blob.type,
      });

      if (blob.size === 0) {
        logger.error('[SceneStitchingEngine] ❌ Video blob is empty (0 bytes)');
        resolve(false);
        return;
      }

      const video = document.createElement('video');
      const url = URL.createObjectURL(blob);
      
      let validated = false;
      
      video.onloadedmetadata = () => {
        logger.info('[SceneStitchingEngine] Video metadata loaded:', {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
        
        if (!isFinite(video.duration) || video.duration <= 0) {
          logger.error('[SceneStitchingEngine] ❌ Invalid video duration:', video.duration);
          URL.revokeObjectURL(url);
          validated = true;
          resolve(false);
          return;
        }
        
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          logger.error('[SceneStitchingEngine] ❌ Invalid video dimensions:', {
            width: video.videoWidth,
            height: video.videoHeight,
          });
          URL.revokeObjectURL(url);
          validated = true;
          resolve(false);
          return;
        }
        
        logger.info('[SceneStitchingEngine] ✓ Video blob is valid and playable');
        URL.revokeObjectURL(url);
        validated = true;
        resolve(true);
      };
      
      video.onerror = (e) => {
        logger.error('[SceneStitchingEngine] ❌ Video loading error during validation:', e);
        URL.revokeObjectURL(url);
        validated = true;
        resolve(false);
      };
      
      // Timeout fallback (10 seconds for validation)
      setTimeout(() => {
        if (!validated) {
          logger.error('[SceneStitchingEngine] ❌ Video validation timeout (10s)');
          URL.revokeObjectURL(url);
          resolve(false);
        }
      }, 10000);
      
      video.preload = 'metadata';
      video.src = url;
      video.load();
    });
  }, []);

  const extractVideoMetadata = useCallback(async (blob: Blob): Promise<void> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      const url = URL.createObjectURL(blob);
      
      logger.info('[SceneStitchingEngine] Starting metadata extraction', {
        blobSize: blob.size,
        blobType: blob.type,
      });
      
      let metadataExtracted = false;
      
      video.onloadedmetadata = () => {
        logger.info('[SceneStitchingEngine] Metadata loaded:', {
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
        
        if (!isFinite(video.duration) || video.duration <= 0) {
          logger.warn('[SceneStitchingEngine] Invalid duration:', video.duration);
          URL.revokeObjectURL(url);
          resolve();
          return;
        }
        
        const metadata = {
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        };
        setVideoMetadata(metadata);
        setVideoDuration(video.duration);
        metadataExtracted = true;
        
        // Extract thumbnail from first frame after video can play
        video.currentTime = 0.1;
      };
      
      video.onseeked = () => {
        if (!metadataExtracted) return;
        
        logger.info('[SceneStitchingEngine] Video seeked, extracting thumbnail');
        
        const canvas = document.createElement('canvas');
        canvas.width = 320;
        canvas.height = 180;
        const ctx = canvas.getContext('2d');
        
        if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((thumbnailBlob) => {
            if (thumbnailBlob) {
              const thumbnailUrl = URL.createObjectURL(thumbnailBlob);
              setThumbnailUrl(thumbnailUrl);
              logger.info('[SceneStitchingEngine] Thumbnail extracted successfully');
            } else {
              logger.warn('[SceneStitchingEngine] Failed to create thumbnail blob');
            }
            URL.revokeObjectURL(url);
            resolve();
          }, 'image/jpeg', 0.85);
        } else {
          logger.warn('[SceneStitchingEngine] Invalid video dimensions or canvas context');
          URL.revokeObjectURL(url);
          resolve();
        }
      };
      
      video.onerror = (e) => {
        logger.error('[SceneStitchingEngine] Video loading error:', e);
        URL.revokeObjectURL(url);
        resolve();
      };
      
      // Timeout fallback
      setTimeout(() => {
        if (!metadataExtracted) {
          logger.warn('[SceneStitchingEngine] Metadata extraction timeout');
          URL.revokeObjectURL(url);
          resolve();
        }
      }, 5000);
      
      video.preload = 'metadata';
      video.src = url;
      video.load();
    });
  }, []);

  const handleStitchScenes = useCallback(async () => {
    try {
      setStitchingProgress({
        isStitching: true,
        progress: 0,
        status: 'loading',
      });

      // AUTO-FIX: Check if any scenes have duration=0 and fix them automatically
      if (activeProject) {
        const scenesWithZeroDuration = sceneList.filter(s => s.status === 'recorded' && s.duration === 0);
        
        if (scenesWithZeroDuration.length > 0) {
          console.log(`[SceneStitchingEngine] 🔧 Auto-fixing ${scenesWithZeroDuration.length} scenes with missing durations...`);
          setStitchingProgress({
            isStitching: true,
            progress: 5,
            status: 'loading',
          });
          
          try {
            await SceneService.fixSceneDurations(activeProject.id);
            console.log('[SceneStitchingEngine] ✓ Auto-fix completed successfully');
          } catch (error) {
            console.error('[SceneStitchingEngine] ❌ Auto-fix failed:', error);
            // Continue anyway - validation will catch if it's a real problem
          }
        }
      }

      const isValid = await validateScenes();
      if (!isValid) {
        return;
      }

      const { sceneBlobs, sceneData } = await loadSceneChunks();
      if (sceneBlobs.length === 0) {
        setStitchingProgress({
          isStitching: false,
          progress: 0,
          status: 'error',
          error: 'No scene videos found to stitch',
        });
        return;
      }

      const finalBlob = await stitchChunks(sceneBlobs, sceneData);
      
      // SKIP VALIDATION: MediaRecorder+Canvas WebM files often have invalid metadata
      // but can still be playable. Let user download and test the video.
      // Server-side stitching will properly handle metadata.
      logger.warn('[SceneStitchingEngine] ⚠️ Skipping validation - Canvas+MediaRecorder has metadata limitations');
      logger.info('[SceneStitchingEngine] Video blob created:', {
        size: finalBlob.size,
        sizeMB: (finalBlob.size / 1024 / 1024).toFixed(2),
        type: finalBlob.type
      });
      
      setStitchedBlob(finalBlob);

      // Extract video metadata and thumbnail
      await extractVideoMetadata(finalBlob);
      
      // Smooth transition from progress to success view
      setIsTransitioning(true);
      
      // Wait for fade-out animation
      await new Promise((resolve) => setTimeout(resolve, 300));
      
      // Switch to success view
      setShowSuccessView(true);
      setIsTransitioning(false);
      
      // Trigger celebration animation after transition
      setTimeout(() => {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      }, 100);

      if (onComplete) {
        onComplete(finalBlob);
      }

      logger.info('[SceneStitchingEngine] ✅ Stitching completed successfully with valid video');
    } catch (error) {
      logger.error(`[SceneStitchingEngine] ❌ STITCHING PROCESS FAILED: ${error}`);
      
      // Provide detailed error information
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during stitching';
      
      setStitchingProgress({
        isStitching: false,
        progress: 0,
        status: 'error',
        error: errorMessage,
      });
      
      // Clean up any partial blob
      setStitchedBlob(null);
      setVideoDuration(0);
      setVideoMetadata(null);
    }
  }, [validateScenes, loadSceneChunks, stitchChunks, validateVideoBlob, extractVideoMetadata, onComplete, setStitchingProgress]);

  const handleClose = useCallback(() => {
    setStitchingProgress({
      isStitching: false,
      progress: 0,
      status: 'idle',
    });
    setStitchedBlob(null);
    setVideoDuration(0);
    setVideoMetadata(null);
    setShowCelebration(false);
    setShowSuccessView(false);
    setIsTransitioning(false);
    
    // Clean up thumbnail URL to prevent memory leaks
    if (thumbnailUrl) {
      URL.revokeObjectURL(thumbnailUrl);
      setThumbnailUrl(null);
    }
    
    onClose();
  }, [onClose, setStitchingProgress, thumbnailUrl]);

  const handleDownload = useCallback(() => {
    if (!stitchedBlob || !activeProject) return;

    const url = URL.createObjectURL(stitchedBlob);
    const a = document.createElement('a');
    const filename = `${activeProject.title.replace(/\s+/g, '_')}_stitched.mp4`;
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Show success toast
    message.success({
      content: (
        <Space>
          <CheckCircle size={16} />
          <span>Video downloaded successfully as {filename}</span>
        </Space>
      ),
      duration: 4,
    });

    logger.info('[SceneStitchingEngine] MP4 video downloaded:', filename);
  }, [stitchedBlob, activeProject]);

  const handleStitchAnother = useCallback(() => {
    // Reset state and close modal, user can start fresh
    handleClose();
    message.info('Ready to record and stitch another project!');
  }, [handleClose]);

  const handleExportForPlatform = useCallback(() => {
    if (!stitchedBlob) return;
    
    setExportVideoBlobs([stitchedBlob]);
    setIsExportModalVisible(true);
    
    logger.info('[SceneStitchingEngine] Opening platform export modal');
  }, [stitchedBlob, setExportVideoBlobs, setIsExportModalVisible]);

  useEffect(() => {
    if (visible && !stitchingProgress.isStitching && stitchingProgress.status === 'idle') {
      handleStitchScenes();
    }
  }, [visible, stitchingProgress.isStitching, stitchingProgress.status, handleStitchScenes]);

  const getStatusStep = () => {
    switch (stitchingProgress.status) {
      case 'loading':
        return 0;
      case 'stitching':
        return 1;
      case 'encoding':
        return 2;
      case 'complete':
        return 3;
      case 'error':
        return -1;
      default:
        return 0;
    }
  };

  const recordedSceneCount = sceneList.filter((s) => s.status === 'recorded').length;

  // Helper: Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    // Validate input - reject corrupted values
    if (!isFinite(seconds) || seconds <= 0 || seconds > 86400) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper: Get resolution display
  const getResolutionDisplay = (): string => {
    if (!videoMetadata) return 'Unknown';
    const { width, height } = videoMetadata;
    if (height >= 2160) return '4K UHD';
    if (height >= 1080) return '1080p HD';
    if (height >= 720) return '720p HD';
    return `${width}x${height}`;
  };

  // Helper: Count transitions
  const transitionsCount = useMemo(() => {
    return sceneList.filter(s => 
      s.status === 'recorded' && 
      s.transitionOut && 
      s.transitionOut.type !== 'cut'
    ).length;
  }, [sceneList]);

  // Helper: Quick export to platform
  const handleQuickExport = useCallback((platform: 'linkedin' | 'instagram' | 'youtube') => {
    if (!stitchedBlob) return;
    
    setExportVideoBlobs([stitchedBlob]);
    setIsExportModalVisible(true);
    
    logger.info(`[SceneStitchingEngine] Quick export to ${platform}`);
  }, [stitchedBlob, setExportVideoBlobs, setIsExportModalVisible]);

  return (
    <Modal
      title={
        <Space>
          <Film size={20} />
          <span>Stitch Multi-Scene Video</span>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      footer={
        stitchingProgress.status === 'complete'
          ? [
              <Button 
                key="stitch-another" 
                icon={<RefreshCw size={16} />}
                onClick={handleStitchAnother}
              >
                Stitch Another
              </Button>,
              <Button key="close" onClick={handleClose}>
                Close
              </Button>,
              platformExportEnabled && (
                <Button
                  key="export"
                  icon={<Share2 size={16} />}
                  onClick={handleExportForPlatform}
                >
                  Export for Platform
                </Button>
              ),
              <Button
                key="download"
                icon={<Download size={16} />}
                onClick={handleDownload}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderColor: '#667eea',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '14px',
                  height: '40px',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }}
              >
                Download HD Video
              </Button>,
            ].filter(Boolean)
          : stitchingProgress.status === 'error'
          ? [
              <Button key="close" onClick={handleClose}>
                Close
              </Button>,
              <Button key="retry" type="primary" onClick={handleStitchScenes}>
                Retry
              </Button>,
            ]
          : null
      }
      width={600}
      closable={!stitchingProgress.isStitching}
      maskClosable={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Phase 1: Progress View (with fade-out transition) */}
        {!showSuccessView && (
          <div
            className={isTransitioning ? 'phase-transitioning-out' : ''}
            style={{
              opacity: isTransitioning ? 0 : 1,
              transition: 'opacity 300ms ease-out',
            }}
          >
            {/* Show project info during stitching */}
            {activeProject && stitchingProgress.status !== 'complete' && (
              <div>
                <Text strong>{activeProject.title}</Text>
                <br />
                <Text type="secondary">
                  {recordedSceneCount} recorded scene{recordedSceneCount !== 1 ? 's' : ''} • Total duration:{' '}
                  {Math.floor(activeProject.totalDuration / 1000)}s
                </Text>
              </div>
            )}

            {/* Show 4-step progress (including complete step before transition) */}
            <Steps 
              current={getStatusStep()} 
              size="small"
              status={stitchingProgress.status === 'error' ? 'error' : 'process'}
            >
              <Step title="Loading" description="Loading scene chunks" />
              <Step title="Stitching" description="Concatenating scenes" />
              <Step title="Encoding" description="Finalizing video" />
              <Step title="Complete" description="Ready to download" />
            </Steps>
          </div>
        )}

        {/* Error State */}
        {stitchingProgress.status === 'error' && (
          <Alert
            message="Stitching Failed"
            description={stitchingProgress.error || 'An unknown error occurred'}
            type="error"
            showIcon
            icon={<AlertCircle size={16} />}
          />
        )}

        {/* Phase 2: Success View (with fade-in + slide-up transition) */}
        {showSuccessView && (
          <div
            className="phase-success-view"
            style={{
              animation: 'successViewFadeIn 500ms ease-out',
            }}
          >
            {/* Hero Success Section */}
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #bae6fd',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Sparkle decorations */}
              <Sparkles 
                size={24} 
                style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  color: '#0ea5e9',
                  opacity: 0.6,
                }}
                className={showCelebration ? 'sparkle-icon' : ''}
              />
              
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CheckCircle 
                    size={32} 
                    color="#10b981" 
                    fill="#d1fae5"
                    className={showCelebration ? 'celebrate-icon' : ''}
                  />
                  <div>
                    <div style={{ 
                      fontSize: '20px', 
                      fontWeight: 600, 
                      color: '#0f172a',
                      marginBottom: '4px',
                    }}>
                      🎬 Your Professional Video is Ready!
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#64748b',
                    }}>
                      {recordedSceneCount} scenes seamlessly combined{transitionsCount > 0 ? ` with ${transitionsCount} cinematic transition${transitionsCount !== 1 ? 's' : ''}` : ''}
                    </div>
                  </div>
                </div>
                
                {/* Value Proposition Highlights */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '12px',
                  marginTop: '16px',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: '#475569',
                  }}>
                    <Wand2 size={16} color="#8b5cf6" />
                    <span>Professional editing</span>
                  </div>
                  {transitionsCount > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px',
                      color: '#475569',
                    }}>
                      <Zap size={16} color="#f59e0b" />
                      <span>Seamless transitions</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '13px',
                    color: '#475569',
                  }}>
                    <Volume2 size={16} color="#10b981" />
                    <span>Studio-quality audio</span>
                  </div>
                </div>
              </Space>
            </div>

            {/* Video Preview Thumbnail with integrated metadata */}
            {thumbnailUrl && (
              <div style={{
                position: 'relative',
                width: '100%',
                borderRadius: '8px',
                overflow: 'hidden',
                cursor: 'pointer',
                border: '2px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}>
                <img 
                  src={thumbnailUrl} 
                  alt="Video preview"
                  style={{
                    width: '100%',
                    display: 'block',
                  }}
                />
                {/* Play button overlay */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  background: 'rgba(0, 0, 0, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.3s ease',
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
                    e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)';
                  }}
                >
                  <Play size={28} color="white" fill="white" />
                </div>
                {/* Metadata badges overlay at bottom */}
                <div style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '12px',
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}>
                  <Badge 
                    count={videoDuration > 0 ? formatDuration(videoDuration) : '0:00'}
                    style={{ 
                      backgroundColor: 'rgba(24, 144, 255, 0.95)',
                      fontSize: '12px',
                      padding: '3px 10px',
                      height: 'auto',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }} 
                  />
                  <Badge 
                    count={getResolutionDisplay()}
                    style={{ 
                      backgroundColor: 'rgba(82, 196, 26, 0.95)',
                      fontSize: '12px',
                      padding: '3px 10px',
                      height: 'auto',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }} 
                  />
                  <Badge 
                    count="WebM"
                    style={{ 
                      backgroundColor: 'rgba(114, 46, 209, 0.95)',
                      fontSize: '12px',
                      padding: '3px 10px',
                      height: 'auto',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    }} 
                  />
                </div>
              </div>
            )}

            {/* Platform Quick-Picks - Enhanced with brand colors */}
            {platformExportEnabled && (
              <div style={{
                background: 'linear-gradient(to right, #fafafa, #f5f5f5)',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}>
                  <TrendingUp size={16} color="#6366f1" />
                  <Text strong style={{ fontSize: '14px' }}>
                    Share on Social Media
                  </Text>
                </div>
                <Space wrap size="middle">
                  <Tooltip title="Optimized for LinkedIn 1:1">
                    <Button 
                      style={{
                        background: '#0077b5',
                        borderColor: '#0077b5',
                        color: 'white',
                        fontWeight: 500,
                      }}
                      icon={<Linkedin size={16} />}
                      onClick={() => handleQuickExport('linkedin')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#006399';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#0077b5';
                      }}
                    >
                      LinkedIn
                    </Button>
                  </Tooltip>
                  <Tooltip title="Optimized for Instagram 9:16">
                    <Button 
                      style={{
                        background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                        borderColor: '#bc1888',
                        color: 'white',
                        fontWeight: 500,
                      }}
                      icon={<Instagram size={16} />}
                      onClick={() => handleQuickExport('instagram')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      Instagram
                    </Button>
                  </Tooltip>
                  <Tooltip title="Optimized for YouTube 16:9">
                    <Button 
                      style={{
                        background: '#FF0000',
                        borderColor: '#FF0000',
                        color: 'white',
                        fontWeight: 500,
                      }}
                      icon={<Youtube size={16} />}
                      onClick={() => handleQuickExport('youtube')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#cc0000';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#FF0000';
                      }}
                    >
                      YouTube
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            )}
            
            {/* Pro Tip Section - Compact */}
            {platformExportEnabled && (
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                padding: '10px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <Lightbulb size={16} color="#f59e0b" style={{ flexShrink: 0 }} />
                <Text style={{ fontSize: '12px', color: '#78350f' }}>
                  💡 Use platform-optimized exports for best results on social media
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Progress bar for in-progress stitching */}
        {!showSuccessView && stitchingProgress.isStitching && stitchingProgress.status !== 'error' && (
          <>
            <Progress
              percent={stitchingProgress.progress}
              status={'active'}
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#52c41a',
              }}
            />

            {stitchingProgress.currentScene !== undefined && stitchingProgress.totalScenes !== undefined && (
              <Text type="secondary">
                Processing scene {stitchingProgress.currentScene} of {stitchingProgress.totalScenes}...
              </Text>
            )}
          </>
        )}

        {stitchedBlob && stitchingProgress.status !== 'complete' && (
          <div>
            <Text type="secondary">
              Final video size: {(stitchedBlob.size / 1024 / 1024).toFixed(2)} MB
            </Text>
          </div>
        )}
      </Space>
      
      <style>{`
        @keyframes celebrate {
          0%, 100% { 
            transform: scale(1) rotate(0deg); 
          }
          15% { 
            transform: scale(1.3) rotate(-15deg); 
          }
          30% { 
            transform: scale(1.2) rotate(15deg); 
          }
          45% { 
            transform: scale(1.35) rotate(-12deg); 
          }
          60% { 
            transform: scale(1.25) rotate(8deg); 
          }
          75% { 
            transform: scale(1.15) rotate(-5deg); 
          }
        }
        
        @keyframes sparkle {
          0%, 100% { 
            transform: scale(1) rotate(0deg);
            opacity: 0.6;
          }
          25% { 
            transform: scale(1.2) rotate(90deg);
            opacity: 1;
          }
          50% { 
            transform: scale(0.9) rotate(180deg);
            opacity: 0.8;
          }
          75% { 
            transform: scale(1.15) rotate(270deg);
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
        }
        
        @keyframes successViewFadeIn {
          0% { 
            opacity: 0;
            transform: translateY(20px);
          }
          100% { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .celebrate-icon {
          animation: celebrate 0.8s ease-in-out, pulse 1.5s ease-in-out 0.8s;
        }
        
        .sparkle-icon {
          animation: sparkle 2s ease-in-out infinite;
        }
        
        .phase-success-view {
          animation: successViewFadeIn 500ms ease-out;
        }
      `}</style>
    </Modal>
  );
};

export default SceneStitchingEngine;
